import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, Stack } from '@mui/material';

import { Alert, Text, api, isDefined, useCopyToClipboard } from 'common';

import { getNodeOutputHandle } from './getNodeOutputHandle';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockType } from '../../../types';
import type { OutputContentProps } from '../../types';

interface ConditionalDisplayItem {
  alias: string;
  value: boolean;
  id: string;
}

function getConditionalDisplayItems(
  output: Record<string, boolean> | undefined,
  config: api.FlowDefinition.Conditional.ConditionalBlockConfig | undefined
): ConditionalDisplayItem[] {
  if (!isDefined(output)) {
    return [];
  }

  const items: ConditionalDisplayItem[] = [];
  const processedKeys = new Set<string>();

  // Process conditions in the order they appear in config
  if (isDefined(config?.conditions)) {
    for (const condition of config.conditions) {
      if (isDefined(output[condition.id])) {
        const handle = getNodeOutputHandle(condition);
        items.push({
          id: condition.id,
          alias: handle?.handleName ?? condition.id,
          value: output[condition.id],
        });
        processedKeys.add(condition.id);
      }
    }
  }

  // Add any remaining keys that weren't in the config (in theory they all should be in the config except for the '__Else__' key, this is just defensive programming)
  for (const [key, value] of Object.entries(output)) {
    if (
      !processedKeys.has(key) &&
      key !== api.FlowDefinition.Conditional.CONDITIONAL_ELSE_CONDITION_ID
    ) {
      items.push({
        id: key,
        alias: key,
        value,
      });
    }
  }

  // Add '__Else__' if it exists
  if (isDefined(output[api.FlowDefinition.Conditional.CONDITIONAL_ELSE_CONDITION_ID])) {
    items.push({
      id: api.FlowDefinition.Conditional.CONDITIONAL_ELSE_CONDITION_ID,
      alias: 'Else',
      value: output[api.FlowDefinition.Conditional.CONDITIONAL_ELSE_CONDITION_ID],
    });
    processedKeys.add(api.FlowDefinition.Conditional.CONDITIONAL_ELSE_CONDITION_ID);
  }

  return items;
}

export function ConditionalNodeOutputContent(
  props: OutputContentProps<api.FlowDefinition.ProducedType.Any>
) {
  const { selectedNode } = useReactFlowViewContext();
  const { copyToClipboard } = useCopyToClipboard();
  if (selectedNode?.data.uiBlockType !== BlockType.Conditional) {
    return null;
  }
  const config = selectedNode?.data.config as
    api.FlowDefinition.Conditional.ConditionalBlockConfig | undefined;
  const returnValue = props.returnValue as Record<string, boolean> | undefined;

  const returnValueTyped = returnValue;
  const conditionalItems = getConditionalDisplayItems(returnValueTyped, config);

  return (
    <Stack spacing={2}>
      {conditionalItems.length > 0 && (
        <Stack spacing={1}>
          {conditionalItems.map((item) => (
            <Stack key={item.id} direction="row" spacing={1} alignItems="center">
              <Text variant="body2" bold>
                {item.alias}:
              </Text>
              <Text variant="body2">{String(item.value)}</Text>
            </Stack>
          ))}
        </Stack>
      )}
      <Button
        variant="text"
        startIcon={<ContentCopyIcon />}
        onClick={() => copyToClipboard(JSON.stringify(returnValueTyped, null, 2))}
        sx={{ alignSelf: 'flex-start' }}
      >
        Copy raw output
      </Button>
      <Alert variant="info">The raw output can be used to mock the block output.</Alert>
    </Stack>
  );
}
